import React from 'react';
import { ptIfSet, ptOfListItem, ptOfSlider } from './calc.js';
import { Field, KiBanner, Pill, PtBadge, PtSliderMeta, RadioList, Segmented, Slider } from './components.jsx';
import { AI_FUNCS, BASIS_FUNCS, HILFS_FUNCS, MODULES, STACKCMS_ADDONS, STACKCMS_BASIS_ID, STACKCMS_FUNCS } from './config.js';
import { Drawer } from './drawers.jsx';
import { currentLocale, t, tLabel } from './i18n.js';
import { getModuleDetail } from './module-details.js';
const { useState } = React;

// ============================================================================
// SEITE 1 — Umfang & Funktionen
// Block 1 Module = "Karten + Detail-Drawer" (v4)
// ============================================================================

// Helper: Map<id, PT> für Segmented/RadioList — pro Option ein Marginal-Effekt
const _ptMapForOptions = (state, path, optionIds) => {
  const map = {};
  for (const id of optionIds) map[id] = ptIfSet(state, path, id);
  return map;
};

// ============================================================================
// Module-Karte mit Info-Button (öffnet Drawer)
// ============================================================================
const ModuleCard = ({ mod, sum, selected, disabled, lockedHint, onToggle, onDetails, badge }) => (
  <button
    type="button"
    className={`mod-card${selected ? ' is-selected' : ''}${disabled ? ' is-locked' : ''}`}
    onClick={disabled ? undefined : onToggle}
    aria-pressed={selected}
    title={lockedHint || undefined}
  >
    <span className="mod-card__head">
      <span className="mod-card__icon"><i className={`fa-solid fa-${mod.icon}`}></i></span>
      <span
        className="mod-card__info"
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); onDetails(mod.id); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onDetails(mod.id); } }}
        title={t('p1.module_details_title')}
        aria-label={t('p1.module_details_aria', { label: mod.label })}
      >
        <i className="fa-regular fa-circle-question"></i>
      </span>
    </span>
    <span className="mod-card__title">{mod.label}</span>
    {sum && <span className="mod-card__sum">{sum}</span>}
    {(selected || (disabled && !lockedHint)) && (
      <span className="mod-card__locked" aria-label={disabled ? t('p1.module_always_included') : t('p1.module_selected')}>
        <i className="fa-solid fa-check"></i>
      </span>
    )}
    {badge}
  </button>
);

// ============================================================================
// Module-Detail Drawer
// ============================================================================
const ModuleDetailDrawer = ({ moduleId, group, selected, onClose, onToggle }) => {
  const mod = !moduleId ? null
    : (BASIS_FUNCS.find(m => m.id === moduleId)
      || HILFS_FUNCS.find(m => m.id === moduleId)
      || AI_FUNCS.find(m => m.id === moduleId)
      || STACKCMS_FUNCS.find(m => m.id === moduleId)
      || MODULES.find(m => m.id === moduleId));
  const detail = mod ? getModuleDetail(mod.id, currentLocale()) : null;
  const isLocked = group === 'basis';
  const isSel = selected;
  const open = !!mod;

  // Close on Esc
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const groupLabel = group === 'basis'    ? t('p1.drawer_cat_basis')
                   : group === 'hilfs'    ? t('p1.drawer_cat_hilfs')
                   : group === 'ai'       ? t('p1.drawer_cat_ai')
                   : group === 'stackcms' ? t('p1.drawer_cat_stackcms')
                                          : t('p1.drawer_cat_module');

  return (
    <React.Fragment>
      <div className={`mod-drawer-scrim${open ? ' is-open' : ''}`} onClick={onClose}></div>
      <aside className={`mod-drawer${open ? ' is-open' : ''}`} role="dialog" aria-modal="true" aria-hidden={!open}>
        {mod && detail && (
          <React.Fragment>
            <div className="mod-drawer__head">
              <div>
                <div className="mod-drawer__cat">{groupLabel}</div>
                <h2 className="mod-drawer__title">
                  <span className="mod-drawer__icon"><i className={`fa-solid fa-${mod.icon}`}></i></span>
                  {mod.label}
                </h2>
              </div>
              <button type="button" className="mod-drawer__close" onClick={onClose} aria-label={t('common.close')}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <div className="mod-drawer__body">
              <p className="mod-drawer__lead">{detail.long}</p>

              <div className="mod-drawer__block">
                <h4>{t('p1.drawer_section_scope')}</h4>
                <ul className="mod-drawer__list">
                  {detail.items.map((it, i) => <li key={i}>{it}</li>)}
                </ul>
              </div>

              {detail.benefit && (
                <div className="mod-drawer__block">
                  <h4>{t('p1.drawer_section_benefit')}</h4>
                  <p>{detail.benefit}</p>
                </div>
              )}

              {detail.example && (
                <div className="mod-drawer__block">
                  <h4>{t('p1.drawer_section_example')}</h4>
                  <p style={{ fontStyle: 'italic' }}>{detail.example}</p>
                </div>
              )}
            </div>

            <div className="mod-drawer__foot">
              {isLocked ? (
                <div className="mod-drawer__locked-note">
                  <i className="fa-solid fa-lock"></i>
                  {t('p1.drawer_locked_note')}
                </div>
              ) : (
                <button type="button" className="btn" data-kind={isSel ? 'quiet' : 'primary'} onClick={onToggle}>
                  <i className={`fa-solid ${isSel ? 'fa-minus' : 'fa-plus'}`}></i>
                  <span>{isSel ? t('p1.drawer_deselect') : t('p1.drawer_select')}</span>
                </button>
              )}
              <button type="button" className="btn" data-kind="quiet" onClick={onClose}>{t('common.close')}</button>
            </div>
          </React.Fragment>
        )}
      </aside>
    </React.Fragment>
  );
};

// ============================================================================
// AiTagList — KI-erkannte Eigenfunktionen als toggle-bare Chips mit Tooltip.
// Verwendet in Block 2 (Custom-Funktionen). Sync mit Slider customCore/Support.
// ============================================================================
const AiTagList = ({ kind, features, onToggle }) => {
  if (!features || features.length === 0) return null;
  const activeCount = features.filter(f => f.active).length;
  const total = features.length;
  const headlineKey = kind === 'core' ? 'p1.ai_features_headline_core_one' : 'p1.ai_features_headline_support_one';
  const headlineKeyOther = kind === 'core' ? 'p1.ai_features_headline_core_other' : 'p1.ai_features_headline_support_other';
  const headline = total === 1 ? t(headlineKey, { count: total }) : t(headlineKeyOther, { count: total });

  return (
    <div className="ai-taglist" data-kind={kind}>
      <div className="ai-taglist__head">
        <span className="ai-taglist__title">
          <i className="fa-solid fa-wand-magic-sparkles"></i>
          <span>{headline}</span>
        </span>
        <span className="ai-taglist__count">{t('p1.ai_features_active_count', { active: activeCount, total })}</span>
      </div>
      <div className="ai-taglist__chips">
        {features.map(f => (
          <button
            key={f.id}
            type="button"
            className={`ai-tag${f.active ? ' is-active' : ''}`}
            onClick={() => onToggle(f.id)}
            aria-pressed={f.active}
          >
            <span className="ai-tag__icon" aria-hidden="true">
              <i className={`fa-solid ${f.active ? 'fa-check' : 'fa-plus'}`}></i>
            </span>
            <span className="ai-tag__label">{f.label}</span>
            <span className="ai-tag__tip" role="tooltip">
              <span className="ai-tag__tip-desc">{f.description}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

const Page1 = ({ state, setState, openDrawer }) => {
  const { p1, pre } = state;
  const setP1 = (patch) => setState(s => ({ ...s, p1: { ...s.p1, ...patch } }));
  const toggle = (key, id) => {
    const arr = p1[key] || [];
    setP1({ [key]: arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id] });
  };

  // Toggle eines KI-erkannten Feature-Tags. Hält den Slider-Counter in sync:
  // aktiv → +1, inaktiv → −1 (mit Math.max(0, …) als Untergrenze).
  const toggleFeature = (featuresKey, counterKey, id) => {
    const list = p1[featuresKey] || [];
    const next = list.map(f => f.id === id ? { ...f, active: !f.active } : f);
    const delta = next.filter(f => f.active).length - list.filter(f => f.active).length;
    setP1({
      [featuresKey]: next,
      [counterKey]: Math.max(0, (p1[counterKey] || 0) + delta),
    });
  };

  // Stack-CMS-Validierung: Add-ons setzen Basis voraus.
  // Wird die Basis abgewählt, müssen Add-ons mit-abgewählt werden.
  const toggleStackcms = (id) => {
    const arr = p1.stackcms || [];
    const isIn = arr.includes(id);
    if (id === STACKCMS_BASIS_ID && isIn) {
      // Basis abwählen → alle Add-ons mit entfernen
      setP1({ stackcms: [] });
      return;
    }
    if (STACKCMS_ADDONS.includes(id) && !arr.includes(STACKCMS_BASIS_ID)) {
      // Add-on ohne Basis → blockieren (UI sollte das eh disabled rendern)
      return;
    }
    setP1({ stackcms: isIn ? arr.filter(x => x !== id) : [...arr, id] });
  };
  const isStackcmsAddon  = (id) => STACKCMS_ADDONS.includes(id);
  const isStackcmsLocked = (id) => isStackcmsAddon(id) && !(p1.stackcms || []).includes(STACKCMS_BASIS_ID);

  // Drawer state für Modul-Details
  const [drawer, setDrawer] = useState({ id: null, group: null });
  const openDetails = (group) => (id) => setDrawer({ id, group });
  const closeDetails = () => setDrawer({ id: null, group: null });

  const dismissKiBanner = () => setState(s => ({ ...s, ki_metadata: { ...(s.ki_metadata || {}), bannerDismissed: true } }));

  // Calc-Overlay: pro Option Marginal-PT-Werte
  const ptCustomComplexity = _ptMapForOptions(state, 'p1.customComplexity', ['easy','medium','complex']);
  const ptLanguages        = _ptMapForOptions(state, 'p1.languages',        ['none','two','many']);
  const ptPerformance      = _ptMapForOptions(state, 'p1.performance',      ['standard','latency','strict']);
  const ptAuditability     = _ptMapForOptions(state, 'p1.auditability',     ['standard','extended','regulatory']);
  const ptCustomCore       = ptOfSlider(state, 'p1.customCore');
  const ptCustomSupport    = ptOfSlider(state, 'p1.customSupport');

  // Helper: rendert eine ModuleCard mit Detail-Texte, Toggle-Logik und PT-Badge
  const renderCard = ({ mod, group, isLocked, onToggleCustom }) => {
    const selArr = isLocked ? null : (p1[group] || []);
    const sel = isLocked || (selArr && selArr.includes(mod.id));
    const detail = getModuleDetail(mod.id, currentLocale());
    const stackcmsBlocked = group === 'stackcms' && isStackcmsLocked(mod.id);
    const disabled = isLocked || stackcmsBlocked;
    const lockedHint = stackcmsBlocked ? t('p1.stackcms_addon_requires_basis') : null;
    // PT-Badge: nicht für Basis (zählt nicht in CapP)
    const badge = isLocked ? null
      : <PtBadge value={ptOfListItem(state, `p1.${group}`, mod.id)} dim={!sel}/>;
    return (
      <ModuleCard
        key={mod.id}
        mod={mod}
        sum={detail?.sum}
        selected={!!sel}
        disabled={disabled}
        lockedHint={lockedHint}
        onToggle={() => (onToggleCustom || (() => toggle(group, mod.id)))()}
        onDetails={openDetails(group)}
        badge={badge}
      />
    );
  };

  return (
    <div style={{ maxWidth: 980, margin: '0 auto' }}>
      <KiBanner state={state} onDismiss={dismissKiBanner}/>
      <header style={{ marginBottom: 24 }}>
        <span className="block__cat">{t('p1.section_label')}</span>
        <h1 className="block__title" style={{ fontSize: 24, marginTop: 6 }}>{t('p1.headline')}</h1>
      </header>

      {/* === Block 1: Module === */}
      <div className="block is-card">
        <div className="block__head">
          <div>
            <span className="block__cat">{t('p1.block_modules_cat')}</span>
            <h2 className="block__title">{t('p1.block_modules_title')}</h2>
          </div>
        </div>

        <div className="mod-ai-banner" style={{ marginBottom: 16 }}>
          <i className="fa-solid fa-circle-info"></i>
          <span>{t('p1.block_modules_stack_banner')}</span>
        </div>

        {/* Basis */}
        <div className="subblock">
          <div className="subblock__title" style={{ justifyContent: 'space-between' }}>
            <span><i className="fa-solid fa-cube" style={{ marginRight: 8, color: 'var(--text-color-muted)' }}></i>{t('p1.subblock_basis_label')} <span className="muted" style={{ fontWeight: 400 }}>· {t('p1.subblock_basis_note')}</span></span>
            <Pill tone="muted" hideMobile>{t('p1.subblock_basis_pill')}</Pill>
          </div>
          <div className="mod-grid">
            {BASIS_FUNCS.map(f => renderCard({ mod: { ...f, label: tLabel('modules.basis', f) }, group: 'basis', isLocked: true }))}
          </div>
        </div>

        {/* Hilfs */}
        <div className="subblock">
          <div className="subblock__title">
            <i className="fa-solid fa-puzzle-piece" style={{ marginRight: 8, color: 'var(--text-color-muted)' }}></i>
            {t('p1.subblock_hilfs_label')} <span className="muted" style={{ fontWeight: 400 }}>· {t('p1.subblock_hilfs_note')}</span>
          </div>
          <div className="mod-grid">
            {HILFS_FUNCS.map(f => renderCard({ mod: { ...f, label: tLabel('modules.hilfs', f) }, group: 'hilfs', isLocked: false }))}
          </div>
        </div>

        {/* KI */}
        <div className="subblock">
          <div className="subblock__title" style={{ justifyContent: 'space-between' }}>
            <span>
              <i className="fa-solid fa-wand-magic-sparkles" style={{ marginRight: 8, color: 'var(--primary-60)' }}></i>
              {t('p1.subblock_ai_label')} <span className="muted" style={{ fontWeight: 400 }}>· {t('p1.subblock_ai_note')}</span>
            </span>
            <Pill tone="primary" hideMobile>{t('p1.subblock_ai_pill')}</Pill>
          </div>
          <div className="mod-grid">
            {AI_FUNCS.map(f => renderCard({ mod: { ...f, label: tLabel('modules.ki', f) }, group: 'ai', isLocked: false }))}
          </div>
        </div>

        {/* Stack CMS by cloudtec */}
        <div className="subblock">
          <div className="subblock__title" style={{ justifyContent: 'space-between' }}>
            <span>
              <i className="fa-solid fa-layer-group" style={{ marginRight: 8, color: 'var(--text-color-muted)' }}></i>
              {t('p1.subblock_stackcms_label')} <span className="muted" style={{ fontWeight: 400 }}>· {t('p1.subblock_stackcms_note')}</span>
            </span>
            <Pill tone="muted" hideMobile>{t('p1.subblock_stackcms_pill')}</Pill>
          </div>
          <div className="mod-grid">
            {STACKCMS_FUNCS.map(f => renderCard({
              mod: { ...f, label: tLabel('modules.stackCms', f) },
              group: 'stackcms',
              isLocked: false,
              onToggleCustom: () => toggleStackcms(f.id),
            }))}
          </div>
        </div>

        {/* cloudtec Module */}
        <div className="subblock">
          <div className="subblock__title" style={{ justifyContent: 'space-between' }}>
            <span><i className="fa-solid fa-cubes" style={{ marginRight: 8, color: 'var(--text-color-muted)' }}></i>{t('p1.subblock_modules_label')} <span className="muted" style={{ fontWeight: 400 }}>· {t('p1.subblock_modules_note')}</span></span>
            <Pill tone="muted" hideMobile><i className="fa-solid fa-circle-info"></i> {t('p1.subblock_modules_pill')}</Pill>
          </div>
          <div className="mod-grid">
            {MODULES.map(f => renderCard({ mod: { ...f, label: tLabel('modules.module', f) }, group: 'modules', isLocked: false }))}
          </div>
        </div>
      </div>

      {/* === Block 2: Custom-Funktionen === */}
      <div className="block is-card">
        <div className="block__head">
          <div>
            <span className="block__cat">{t('p1.block_custom_cat')}</span>
            <h2 className="block__title">{t('p1.block_custom_title')}</h2>
            <p className="block__lead">{t('p1.block_custom_lead')}</p>
          </div>
        </div>

        <div className="grid-2" style={{ gap: 24 }}>
          <Field label={t('p1.custom_core_label')} hint={t('p1.custom_core_hint')}>
            <div className="slider--violet">
              <Slider value={p1.customCore} onChange={(v) => setP1({ customCore: v })} min={0} max={10}/>
            </div>
            <PtSliderMeta total={ptCustomCore.total} perUnit={ptCustomCore.perUnit}/>
            <AiTagList
              kind="core"
              features={p1.customCoreFeatures || []}
              onToggle={(id) => toggleFeature('customCoreFeatures', 'customCore', id)}
            />
          </Field>
          <Field label={t('p1.custom_support_label')} hint={t('p1.custom_support_hint')}>
            <div className="slider--violet">
              <Slider value={p1.customSupport} onChange={(v) => setP1({ customSupport: v })} min={0} max={20}/>
            </div>
            <PtSliderMeta total={ptCustomSupport.total} perUnit={ptCustomSupport.perUnit}/>
            <AiTagList
              kind="support"
              features={p1.customSupportFeatures || []}
              onToggle={(id) => toggleFeature('customSupportFeatures', 'customSupport', id)}
            />
          </Field>
        </div>

        <div className="subblock">
          <Field label={t('p1.custom_complexity_label')}>
            <Segmented
              options={[
                { id: 'easy',    label: t('p1.custom_complexity_easy_label'),    sub: t('p1.custom_complexity_easy_sub') },
                { id: 'medium',  label: t('p1.custom_complexity_medium_label'),  sub: t('p1.custom_complexity_medium_sub') },
                { id: 'complex', label: t('p1.custom_complexity_complex_label'), sub: t('p1.custom_complexity_complex_sub') },
              ]}
              value={p1.customComplexity}
              onChange={(v) => setP1({ customComplexity: v })}
              stack
              ptValues={ptCustomComplexity}
            />
          </Field>
        </div>
      </div>

      {/* === Block 3: Querschnittliches === */}
      <div className="block is-card">
        <div className="block__head">
          <div>
            <span className="block__cat">{t('p1.block_cross_cat')}</span>
            <h2 className="block__title">{t('p1.block_cross_title')}</h2>
            <p className="block__lead">{t('p1.block_cross_lead')}</p>
          </div>
        </div>

        <div className="stack-md">
          <Field label={t('p1.languages_label')}>
            <Segmented
              options={[
                { id: 'none', label: t('p1.languages_none') },
                { id: 'two',  label: t('p1.languages_two') },
                { id: 'many', label: t('p1.languages_many') },
              ]}
              value={p1.languages}
              onChange={(v) => setP1({ languages: v })}
              ptValues={ptLanguages}
            />
          </Field>
          <Field label={t('p1.perf_label')} hint={t('p1.perf_hint')}>
            <Segmented
              options={[
                { id: 'standard', label: t('p1.perf_standard_label'), sub: t('p1.perf_standard_sub') },
                { id: 'latency',  label: t('p1.perf_latency_label'),  sub: t('p1.perf_latency_sub') },
                { id: 'strict',   label: t('p1.perf_strict_label'),   sub: t('p1.perf_strict_sub') },
              ]}
              value={p1.performance}
              onChange={(v) => setP1({ performance: v })}
              stack
              ptValues={ptPerformance}
            />
          </Field>
          <Field label={t('p1.audit_label')} hint={t('p1.audit_hint')}>
            <Segmented
              options={[
                { id: 'standard',   label: t('p1.audit_standard_label'),   sub: t('p1.audit_standard_sub') },
                { id: 'extended',   label: t('p1.audit_extended_label'),   sub: t('p1.audit_extended_sub') },
                { id: 'regulatory', label: t('p1.audit_regulatory_label'), sub: t('p1.audit_regulatory_sub') },
              ]}
              value={p1.auditability}
              onChange={(v) => setP1({ auditability: v })}
              stack
              ptValues={ptAuditability}
            />
          </Field>
        </div>
      </div>

      <ModuleDetailDrawer
        moduleId={drawer.id}
        group={drawer.group}
        selected={drawer.group === 'basis' || (drawer.id ? (p1[drawer.group] || []).includes(drawer.id) : false)}
        onClose={closeDetails}
        onToggle={() => {
          if (drawer.group === 'basis') { closeDetails(); return; }
          if (drawer.group === 'stackcms') toggleStackcms(drawer.id);
          else toggle(drawer.group, drawer.id);
          closeDetails();
        }}
      />
    </div>
  );
};

export { Page1, ModuleCard, ModuleDetailDrawer };
